import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import useSEO from "../lib/useSEO";
import useReveal from "../lib/useReveal";
import SectionHeading from "../components/SectionHeading";
import { IMG } from "../lib/media";

const CATEGORIES = [
  "Bakery & Food Stories",
  "Menu Launches",
  "Local St Neots Updates",
  "Wholesale Insights",
  "Behind the Scenes",
  "Offers & Seasonal Specials",
];

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCat, setActiveCat] = useState("all");
  const ref = useReveal([posts, activeCat]);

  useSEO({
    title: "The SugaSpice Journal — Bakery stories, menu launches, St Neots",
    description: "Fresh from the SugaSpice kitchen: menu launches, St Neots updates, wholesale insights and behind-the-scenes stories from our bakery.",
    image: IMG.pastries_mixed,
  });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/blog/posts");
      setPosts(r.data || []);
    } catch {
      toast.error("Couldn't load the journal.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const visible = activeCat === "all" ? posts : posts.filter((p) => p.category === activeCat);
  const featured = visible[0];
  const rest = visible.slice(1);

  return (
    <div ref={ref}>
      <section className="pt-12 md:pt-16 pb-12 bg-[#F7F5F0]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8 fade-up">
              <span className="text-[11px] font-semibold tracking-[0.28em] uppercase text-[#C8102E]">The journal</span>
              <h1 className="mt-5 font-display text-[clamp(2.8rem,7.5vw,7rem)] leading-[0.9] tracking-tight uppercase">
                Fresh from <br /><span className="text-[#C8102E]">SugaSpice.</span>
              </h1>
              <p className="mt-6 max-w-xl text-base md:text-lg text-[#3a3a3a]">Menu launches, behind-the-scenes from the kitchen, wholesale insights and what's going on in St Neots — straight from the SugaSpice team.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#F7F5F0] border-y border-[#D1C9BE]/60">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-5 relative">
          <div className="flex gap-2 overflow-x-auto no-scrollbar pr-12" data-testid="blog-filters">
          <button onClick={() => setActiveCat("all")} className={`btn-pill shrink-0 text-xs whitespace-nowrap ${activeCat === "all" ? "bg-[#1A1A1A] text-[#F7F5F0]" : "bg-white border border-[#D1C9BE] hover:bg-[#1A1A1A] hover:text-[#F7F5F0]"}`} data-testid="blog-filter-all">All</button>
          {CATEGORIES.map((c) => (
            <button key={c} onClick={() => setActiveCat(c)} className={`btn-pill shrink-0 text-xs whitespace-nowrap ${activeCat === c ? "bg-[#C8102E] text-[#F7F5F0]" : "bg-white border border-[#D1C9BE] hover:bg-[#1A1A1A] hover:text-[#F7F5F0]"}`} data-testid={`blog-filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{c}</button>
          ))}
          </div>
          <div className="pointer-events-none absolute top-0 right-0 h-full w-16 bg-gradient-to-l from-[#F7F5F0] to-transparent" />
        </div>
      </section>

      <section className="py-16 md:py-24 bg-[#F7F5F0]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          {loading ? (
            <div className="flex items-center justify-center py-20 text-[#5C5C5C]"><Loader2 className="h-6 w-6 animate-spin mr-3" /> Loading posts…</div>
          ) : visible.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-[#5C5C5C]">No posts in this category yet — check back soon.</p>
            </div>
          ) : (
            <>
              {featured && (
                <Link to={`/blog/${featured.slug}`} className="group block fade-up mb-16" data-testid="blog-featured">
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                    <div className="lg:col-span-7 aspect-[16/10] rounded-3xl overflow-hidden">
                      <img loading="lazy" decoding="async" src={featured.cover_image || IMG.pastries_mixed} alt={featured.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    </div>
                    <div className="lg:col-span-5">
                      <span className="text-[11px] font-semibold tracking-[0.22em] uppercase text-[#C8102E]">{featured.category}</span>
                      <h2 className="mt-3 font-display text-4xl md:text-5xl uppercase tracking-tight leading-[0.95] group-hover:text-[#C8102E] transition-colors">{featured.title}</h2>
                      <p className="mt-4 text-[#3a3a3a]">{featured.excerpt}</p>
                      <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold tracking-[0.18em] uppercase">Read story <ArrowUpRight className="h-4 w-4" /></span>
                    </div>
                  </div>
                </Link>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                {rest.map((p, i) => (
                  <Link to={`/blog/${p.slug}`} key={p.id} className="group fade-up bg-white rounded-3xl overflow-hidden border border-[#D1C9BE]/60 hover:shadow-xl hover:-translate-y-1 transition-all" style={{ transitionDelay: `${i * 50}ms` }} data-testid={`blog-card-${i}`}>
                    <div className="aspect-[4/3] overflow-hidden">
                      <img loading="lazy" decoding="async" src={p.cover_image || IMG.pastries_mixed} alt={p.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    </div>
                    <div className="p-6">
                      <span className="text-[10px] font-semibold tracking-[0.2em] uppercase text-[#C8102E]">{p.category}</span>
                      <h3 className="mt-2 font-display text-xl md:text-2xl uppercase tracking-tight leading-tight group-hover:text-[#C8102E] transition-colors">{p.title}</h3>
                      <p className="mt-3 text-sm text-[#5C5C5C] leading-relaxed line-clamp-3">{p.excerpt}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
